import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useUpdateConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import { useUpdateconsumfinalMutation, useGetconsumfinalQuery } from '../../../../features/consumfinal/consumfinalApiSlice';
import { useGetconsummasterQuery } from '../../../../features/consummaster/consummasterApiSlice';
import { useGetpartnumberQuery } from '../../../../features/partnumber/partnumberApiSlice';
import { useGetModelNumbersQuery } from '../../../../features/modelnumber/modelNumberApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import ModifyConsumablesModal from './ModifyConsumablesModal';
import { useAlert } from '../../../ui/AlertProvider';

// Styled components defined outside the component for better performance
const StyledButton = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ForwardButton = styled(StyledButton)`
  background-color: #4caf50;
`;

const RejectButton = styled(StyledButton)`
  background-color: #f44336;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
  margin-top: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const SelectedAssetsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const SelectedAssetsTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SelectedAssetsTitle = styled.h3`
  margin-bottom: 0;
  color: #1976d2;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorCloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 0 5px;
`;

const StoreL1InboxFormCIR = ({ row, handleBack }) => {
  const [remark, setRemark] = useState('');
  const [openModifyModal, setOpenModifyModal] = useState(false);
  const [selectedConsumables, setSelectedConsumables] = useState([]);
  const [error, setError] = useState('');
  const [isUpdatingReq, setIsUpdatingReq] = useState(false);
  
  // Auth and office data
  const auth = useAuth();
  const alert = useAlert();
  const user_current_office = useSelector((state) => state.office.off);
  
  // API queries with better error handling
  const { getSingleuser, isError: isUsersError, error: usersError } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data, isError, error }) => ({
      getSingleuser: data?.entities,
      isError,
      error
    }),
  });

  const { officedata, isError: isOfficesError, error: officesError } = useGetofficesQuery('offices', {
    selectFromResult: ({ data, isError, error }) => ({
      officedata: data?.entities,
      isError,
      error
    }),
  });

  const { storeoffices, isError: isStoreOfficesError, error: storeOfficesError } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data, isError, error }) => ({
      storeoffices: data?.entities,
      isError,
      error
    }),
  });

  // Master data queries for part number selection
  useGetconsummasterQuery('cosummaster');

  const { partnumber } = useGetpartnumberQuery('partno', {
    selectFromResult: ({ data }) => ({
      partnumber: data?.entities
    }),
  });

  const { modelnumber } = useGetModelNumbersQuery('modelnumber', {
    selectFromResult: ({ data }) => ({  
      modelnumber: data?.entities
    }),
  });
  
  // Mutations with better error handling
  const [updateConsumableReq] = useUpdateConsumableReqMutation();
  const [updateconsumfinal] = useUpdateconsumfinalMutation();

  // Get consumable inventory data - fetch all for the store so Store L1 can see available quantities
  const { 
    consumfinalData, 
    isError: isConsumFinalError, 
    error: consumFinalError 
  } = useGetconsumfinalQuery({
    store: user_current_office
  }, {
    selectFromResult: ({ data, isError, error }) => {
      // Transform normalized entities into an object indexed by partno for easy lookup
      const consumfinalByPartno = {};
      if (data?.entities) {
        Object.values(data.entities).forEach(item => {
          if (item?.partno) {
            consumfinalByPartno[item.partno] = item;
          }
        });
      }
      return {
        consumfinalData: consumfinalByPartno,
        isError,
        error
      };
    },
    skip: !user_current_office,
    refetchOnMountOrArgChange: true
  });

  // Set general error state when any API error occurs
  // Only show errors when not actively updating to avoid stale error states
  useEffect(() => {
    // Don't show mutation errors while we're actively updating - they're handled in try-catch
    if (isUpdatingReq) {
      return;
    }
    
    if (isUsersError) {
      setError(`Error fetching users: ${usersError?.data?.message || 'Unknown error'}`);
    } else if (isOfficesError) {
      setError(`Error fetching offices: ${officesError?.data?.message || 'Unknown error'}`);
    } else if (isStoreOfficesError) {
      setError(`Error fetching store offices: ${storeOfficesError?.data?.message || 'Unknown error'}`);
    } else if (isConsumFinalError) {
      setError(`Error fetching inventory data: ${consumFinalError?.data?.message || 'Unknown error'}`);
    } else {
      // Don't clear error here if we're updating - let the update handler manage it
      if (!isUpdatingReq) {
        setError('');
      }
    }
  }, [
    isUsersError, usersError,
    isOfficesError, officesError,
    isStoreOfficesError, storeOfficesError,
    isConsumFinalError, consumFinalError,
    isUpdatingReq
  ]);

  // Get IDs for workflow - memoize to prevent unnecessary recalculations
  const storeL2_id = useMemo(() => 
    storeoffices ? Object.values(storeoffices).find(item => item?.storeoffice === row?.req_store)?.roleUsers?.L2ReqApprover_C : "",
    [storeoffices, row?.req_store]
  );

  // Get available quantity for a particular partnumber - memoized
  const getAvailableQuantity = useCallback((partno) => {
    if (consumfinalData && partno) {
      const item = consumfinalData[partno];
      return item ? item.balance_order_quantity || 0 : 0;
    }
    return 0;
  }, [consumfinalData]);

  // Helper function to get filtered part numbers based on asset and consumable
  const filteredPartNumbers = useCallback((assetName, brand, consumable) => {
    if (!modelnumber || !partnumber) return [];
  
    const modelNos = Object.values(modelnumber || {})
      .filter(m => m.asset === assetName && m.brand === brand)
      .map(m => m.modelno);
  
    return Object.values(partnumber || {}).filter(pn =>
      pn.consumable === consumable &&
      (
        pn.assetModelNos.includes(assetName) || // Exact asset name match
        pn.assetModelNos.some(model => modelNos.includes(model)) || // Model number match
        pn.assetModelNos.includes(`All-${assetName}`) || // "All-Asset" match
        pn.assetModelNos.includes(`All-${assetName}-${brand}`) // "All-Asset-Brand" match
      )
    ).map(pn => pn.partno);
  }, [modelnumber, partnumber]);

  // Get available part numbers for a consumable - memoized
  // Excludes part numbers already selected in other rows to prevent duplicates
  const getPartNumberOptions = useCallback((consumable, asset, assetTag, currentIndex) => {
    if (!partnumber || !modelnumber) return [];
    
    // Get all possible part numbers for this consumable
    let availablePartNumbers = [];
    
    // If asset is "Not Applicable", return all part numbers for this consumable
    if (asset === "Not Applicable" || asset === null) {
      availablePartNumbers = Object.values(partnumber)
        .filter(pn => pn.consumable === consumable)
        .map(pn => pn.partno);
    } else {
      // If asset is specified, filter by asset and model
      availablePartNumbers = filteredPartNumbers(asset, assetTag?.value?.oem || assetTag?.oem, consumable);
    }
    
    // Filter out part numbers already selected in other rows
    const selectedPartNumbers = selectedConsumables
      .map((item, idx) => idx !== currentIndex ? item.partno : null)
      .filter(partno => partno !== null && partno !== undefined && partno !== '');
    
    return availablePartNumbers.filter(partno => !selectedPartNumbers.includes(partno));
  }, [partnumber, modelnumber, filteredPartNumbers, selectedConsumables]);

  // Get total selected quantity for a group (includes splits of the same group)
  const getGroupTotalQuantity = useCallback((groupId) => {
    return selectedConsumables
      .filter(item => item.groupId === groupId)
      .reduce((sum, item) => sum + (parseInt(item.selectedQuantity, 10) || 0), 0);
  }, [selectedConsumables, alert]);

  // Function to check if partial issue is applicable - memoized to prevent recalculation
  const isPartialIssueCheck = useCallback(() => {
    if (!selectedConsumables.length) return false;
    
    return selectedConsumables.some(item => 
      parseInt(item.selectedQuantity, 10) < parseInt(item.quantity, 10)
    );
  }, [selectedConsumables, alert]);

  // Initialize selectedConsumables from latest request with BO partial issue handling
  useEffect(() => {
    const lastTag = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag;
    const isReturnedFromStoreL2 = lastTag === 'storel2_bts_return_l1';
    
    // If returned from StoreL2, load from store_consum_issue
    if (isReturnedFromStoreL2 && row?.store_consum_issue && row.store_consum_issue.length > 0) {
      const latestStoreIssue = row.store_consum_issue[row.store_consum_issue.length - 1];
      if (latestStoreIssue?.data) {
        // Store consumables already have partno and other details
        const initialConsumables = latestStoreIssue.data.map((item, index) => ({
          ...item,
          groupId: item.groupId || `group-${index}`,
          isSplit: item.isSplit || false,
          originalQuantity: parseInt(item.originalQuantity || item.quantity, 10),
          partno: item.partno || null,
          selectedQuantity: parseInt(item.selectedQuantity || item.quantity, 10),
          modReqQuantity: item.modReqQuantity || parseInt(item.originalQuantity || item.quantity, 10), // Modified requested quantity
          boIssuedQuantity: item.boIssuedQuantity || null,
          isBoIssued: item.isBoIssued || false // Mark if this was issued by BO
        }));
        
        setSelectedConsumables(initialConsumables);
      }
    }
    // Otherwise, load from consumables_requested (when coming from UnitHead)
    else if (row?.consumables_requested && row.consumables_requested.length > 0) {
      const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
      if (latestRequest?.data) {
        // Check if BO has issued consumables
        const boIssuedConsumables = row?.bo_consum_issue?.length > 0 
          ? (row.bo_consum_issue[row.bo_consum_issue.length - 1]?.data || [])
          : [];
        
        const initialConsumables = [];
        let groupCounter = 0;
        
        latestRequest.data.forEach((item, index) => {
          const originalQty = parseInt(item.quantity, 10);
          
          // Find ALL BO issued items matching this consumable (BO may have split into multiple part numbers)
          const matchingBoIssues = boIssuedConsumables.filter(issued => 
            issued.consumable === item.consumable && 
            issued.asset === item.asset && 
            (issued.assettag?.label === item.assettag?.label || issued.assettag === item.assettag)
          );
          
          // Calculate total BO issued for this group
          const totalBoIssued = matchingBoIssues.reduce((sum, issued) => 
            sum + parseInt(issued.quantity, 10), 0
          );
          
          // First, add all BO issued items (each gets its own group for independent splitting)
          matchingBoIssues.forEach((boItem, boIndex) => {
            const boModReqQty = parseInt(boItem.modReqQuantity || boItem.quantity, 10);
            const boGroupId = `group-${groupCounter++}`; // Each BO item gets its own group
            initialConsumables.push({
              consumable: boItem.consumable,
              asset: boItem.asset,
              assettag: boItem.assettag,
              quantity: parseInt(boItem.quantity, 10),
              groupId: boGroupId,
              isSplit: false, // Each BO item is the base of its own group
              originalQuantity: boModReqQty, // Use BO's modReqQuantity as the base for this item's splits
              remainingQuantity: originalQty - totalBoIssued, // What remains for store to handle after BO
              partno: boItem.partno, // Part number already selected by BO
              selectedQuantity: 0, // Store hasn't issued anything - BO items are display only
              modReqQuantity: boModReqQty, // Use modReqQuantity from bo_consum_issue
              boIssuedQuantity: parseInt(boItem.quantity, 10), // Use the actual quantity from this BO item
              isBoIssued: true, // Mark as BO issued (read-only)
              originalRequestedQty: parseInt(boItem.originalRequestedQty || boItem.quantity, 10)
            });
          });
          
          // Only add a Store row if BO has NOT handled this combination at all
          // If BO has issued any quantity (even partially), Store doesn't get a row
          // Store can only handle consumables that BO completely didn't touch
          if (totalBoIssued === 0) {
            const storeGroupId = `group-${groupCounter++}`; // Store item gets its own group
            initialConsumables.push({
              consumable: item.consumable,
              asset: item.asset,
              assettag: item.assettag,
              quantity: originalQty, 
              groupId: storeGroupId,
          isSplit: false,
              originalQuantity: originalQty, // Total Items Requested (actual original request from user)
              remainingQuantity: originalQty, // What remains for store to handle (full amount since BO didn't touch)
          partno: null, // Store should select their own part numbers
          selectedQuantity: 0, // Store hasn't selected anything yet
              modReqQuantity: originalQty, // Modified requested quantity
              boIssuedQuantity: 0, // BO didn't issue anything for this combination
              isBoIssued: false // This is for Store L1 to handle
            });
          }
        });
        
        setSelectedConsumables(initialConsumables);
      }
    }
  }, [row]);


  // Handle changing selected quantity
  // Base rows use their own capacity, split rows borrow from pool
  const handleQuantityChange = useCallback((index, value) => {
    try {
      const newValue = parseInt(value, 10) || 0;
      const item = selectedConsumables[index];
      const currentSelectedQty = parseInt(item.selectedQuantity, 10) || 0;
      const quantityDelta = newValue - currentSelectedQty;
      
      // If no change, return early
      if (quantityDelta === 0) return;
      
      // For Store items (not BO), also check store available quantity if part number is selected
      let validValue = newValue;
      if (!item.isBoIssued && item.partno) {
        const availableQuantity = getAvailableQuantity(item.partno);
        validValue = Math.min(validValue, availableQuantity);
      }
      
      // Check if this item has its own spare capacity
      const itemOwnCapacity = item.modReqQuantity - (item.boIssuedQuantity || 0);
      const itemNeedsToBorrow = validValue > itemOwnCapacity;
      
      // BASE ROW LOGIC: Base rows use their own capacity if they have enough
      // If they don't have enough and need to increase, they borrow from pool
      if (!item.isSplit && !itemNeedsToBorrow) {
        // Base row with sufficient own capacity - use it without affecting others
        validValue = Math.min(validValue, itemOwnCapacity);
        validValue = Math.max(0, validValue);
        
        const updatedConsumables = [...selectedConsumables];
        updatedConsumables[index] = {
          ...updatedConsumables[index],
          selectedQuantity: validValue
        };
        
        setSelectedConsumables(updatedConsumables);
        setError('');
        return;
      }
      
      // POOL BORROWING LOGIC: Split rows OR base rows that need to borrow capacity
      // Find all rows with same Consumable, Asset, and AssetTag (the "pool")
      const poolRows = selectedConsumables.map((row, idx) => ({
        ...row,
        index: idx
      })).filter(row => 
        row.consumable === item.consumable &&
        row.asset === item.asset &&
        (row.assettag?.label === item.assettag?.label)
      );
      
      // Calculate total spare capacity in the pool
      const poolSpareCapacity = poolRows.reduce((sum, row) => {
        const spare = Math.max(0, 
          row.modReqQuantity - (row.boIssuedQuantity || 0) - (parseInt(row.selectedQuantity, 10) || 0)
        );
        return sum + spare;
      }, 0);
      
      // Calculate how much this item needs (for both base rows that borrow and split rows)
      const itemCurrentCapacity = item.modReqQuantity - (item.boIssuedQuantity || 0);
      const itemNeededIncrease = Math.max(0, validValue - itemCurrentCapacity);
      
      // Check if pool has enough spare capacity for the increase needed
      if (itemNeededIncrease > poolSpareCapacity) {
        const errorMsg = `Cannot increase quantity to ${validValue}: Only ${poolSpareCapacity} spare capacity available in pool. Maximum allowed: ${itemCurrentCapacity + poolSpareCapacity}`;
        setError(errorMsg);
        // Don't auto-clear - let user see the error until they fix it or close modal
        return;
      }
      
      const updatedConsumables = [...selectedConsumables];
      
      // Update current item's selectedQuantity
      updatedConsumables[index] = {
        ...updatedConsumables[index],
        selectedQuantity: validValue
      };
      
      // Calculate new modReqQuantity needed for this split item
      const newItemModReqQty = validValue + (item.boIssuedQuantity || 0);
      const modReqDelta = newItemModReqQty - item.modReqQuantity;
      
      // Update this item's modReqQuantity
      updatedConsumables[index] = {
        ...updatedConsumables[index],
        modReqQuantity: newItemModReqQty
      };
      
      // If modReqQuantity increased, take from other rows in the pool
      if (modReqDelta > 0) {
        let remainingToTake = modReqDelta;
        
        // Take from rows with spare capacity (prioritize base rows, then rows with more spare)
        const rowsWithSpare = poolRows
          .filter(row => row.index !== index) // Don't take from current row
          .map(row => ({
            ...row,
            spare: Math.max(0, row.modReqQuantity - (row.boIssuedQuantity || 0) - (parseInt(row.selectedQuantity, 10) || 0))
          }))
          .filter(row => row.spare > 0)
          .sort((a, b) => {
            // Prioritize base rows first, then by spare capacity
            if (!a.isSplit && b.isSplit) return -1;
            if (a.isSplit && !b.isSplit) return 1;
            return b.spare - a.spare;
          });
        
        for (const donorRow of rowsWithSpare) {
          if (remainingToTake <= 0) break;
          
          const canTake = Math.min(donorRow.spare, remainingToTake);
          const donorIndex = donorRow.index;
          
          updatedConsumables[donorIndex] = {
            ...updatedConsumables[donorIndex],
            modReqQuantity: Math.max(0, updatedConsumables[donorIndex].modReqQuantity - canTake)
          };
          
          remainingToTake -= canTake;
        }
      }
      // If modReqQuantity decreased, give back to base rows
      else if (modReqDelta < 0) {
        const toGiveBack = Math.abs(modReqDelta);
        
        // Find base rows in the pool to give back capacity
        const baseRowsInPool = poolRows.filter(row => !row.isSplit && row.index !== index);
        
        if (baseRowsInPool.length > 0) {
          // Give to first base row
          const receiverIndex = baseRowsInPool[0].index;
          updatedConsumables[receiverIndex] = {
            ...updatedConsumables[receiverIndex],
            modReqQuantity: updatedConsumables[receiverIndex].modReqQuantity + toGiveBack
          };
        }
      }
      
      // CRITICAL CHECK: Ensure total modReqQuantity doesn't exceed original total
      const newTotalModReqQuantity = updatedConsumables.reduce((sum, c) => 
        sum + (parseInt(c.modReqQuantity, 10) || 0), 0
      );
      
      const allBaseItems = selectedConsumables.filter(c => !c.isSplit);
      const originalTotalQuantity = allBaseItems.reduce((sum, c) => 
        sum + (parseInt(c.originalQuantity, 10) || 0), 0
      );
      
      // If this change would exceed the total, reject it
      if (newTotalModReqQuantity > originalTotalQuantity) {
        const errorMsg = `Cannot set quantity: Total Requested Qty would become ${newTotalModReqQuantity}, exceeding original total of ${originalTotalQuantity}`;
        setError(errorMsg);
        return;
      }
      
      // Clear any previous error on successful update
      setError('');
      
      setSelectedConsumables(updatedConsumables);
    } catch (err) {
      setError(`Error updating quantity: ${err.message}`);
      console.error("Error handling quantity change:", err);
    }
  }, [selectedConsumables, getAvailableQuantity]);

  // Handler for part number selection
  const handlePartNumberChange = useCallback((index, newPartNumber) => {
    const updatedConsumables = [...selectedConsumables];
    const item = updatedConsumables[index];
    
    // Don't allow changes to BO issued items
    if (item.isBoIssued) {
      return;
    }
    
    // Always keep quantity at 0 when selecting/changing part number - user must manually set quantity
      updatedConsumables[index] = {
        ...updatedConsumables[index],
        partno: newPartNumber,
      selectedQuantity: 0 // Always start at 0, user will set in modal
    };
    
    setSelectedConsumables(updatedConsumables);
  }, [selectedConsumables, alert]);

  // Handler for splitting a consumable row into multiple rows
  const handleSplitRow = useCallback((index) => {
    const itemToSplit = selectedConsumables[index];
    
    // Check if this specific row has spare capacity to split
    // Row spare capacity = Requested Qty - Issued at BO - Issue Qty
    const rowSpareCapacity = Math.max(0,
      itemToSplit.modReqQuantity - (itemToSplit.boIssuedQuantity || 0) - (parseInt(itemToSplit.selectedQuantity, 10) || 0)
    );
    
    if (rowSpareCapacity <= 0) {
      alert.showWarning("Cannot split: This row has no spare capacity. Requested Qty must be greater than (Issued at BO + Issue Qty).");
      return;
    }
    
    // Find the base item to check available capacity
    const baseItem = selectedConsumables.find((c) => c.groupId === itemToSplit.groupId && !c.isSplit);
    if (!baseItem) return;
    
    // Calculate spare capacity in base row: modReqQuantity - boIssuedQuantity - selectedQuantity
    const baseSpareCapacity = Math.max(0, 
      baseItem.modReqQuantity - (baseItem.boIssuedQuantity || 0) - (parseInt(baseItem.selectedQuantity, 10) || 0)
    );
    
    if (baseSpareCapacity <= 0) {
      alert.showWarning("Cannot split: No spare capacity available in base row.");
      return;
    }
    
    // Create a new split row - it should NOT inherit boIssuedQuantity (starts at 0)
    const splitModReqQty = Math.min(1, baseSpareCapacity); // Start with 1 or whatever is available
    const newSplitRow = {
      ...itemToSplit,
      isSplit: true,
      partno: null, // Always null for splits - Store L1 selects part number
      selectedQuantity: 0, // Reset selected quantity
      modReqQuantity: splitModReqQty, // Start with small allocation from base
      boIssuedQuantity: 0, // New splits have no BO issued (they're pure store allocations)
      availableQuantity: itemToSplit.modReqQuantity || itemToSplit.originalQuantity,
      isBoIssued: false // Split rows are Store L1's responsibility (not BO)
    };
    
    // Insert the new split row after the current item
    const updatedConsumables = [
      ...selectedConsumables.slice(0, index + 1),
      newSplitRow,
      ...selectedConsumables.slice(index + 1)
    ];
    
    // Find base item index in updated array
    const baseItemIndex = updatedConsumables.findIndex((c) => c.groupId === itemToSplit.groupId && !c.isSplit);
    
    if (baseItemIndex !== -1) {
      // Reduce base item's modReqQuantity by the split's allocation
      updatedConsumables[baseItemIndex] = {
        ...updatedConsumables[baseItemIndex],
        modReqQuantity: Math.max(0, baseItem.modReqQuantity - splitModReqQty)
      };
    }
    
    setSelectedConsumables(updatedConsumables);
  }, [selectedConsumables, alert]);

  // Handler for removing a split row
  const handleRemoveSplitRow = useCallback((index) => {
    const itemToRemove = selectedConsumables[index];
    
    // Only allow removal of split items, not original items
    if (!itemToRemove.isSplit) {
      alert.showWarning("Cannot remove original request item. You can only remove split rows.");
      return;
    }
    
    // Find the base item for this group
    const baseItemIndex = selectedConsumables.findIndex((c) => c.groupId === itemToRemove.groupId && !c.isSplit);
    
    // Remove the item at the specified index
    const updatedConsumables = selectedConsumables.filter((_, i) => i !== index);
    
    // Give back the removed split's modReqQuantity to the base item
    if (baseItemIndex !== -1) {
      const adjustedIndex = baseItemIndex > index ? baseItemIndex - 1 : baseItemIndex;
      const baseItem = updatedConsumables[adjustedIndex];
      
      // Simply add back the removed split's modReqQuantity to the base item
      updatedConsumables[adjustedIndex] = {
        ...baseItem,
        modReqQuantity: baseItem.modReqQuantity + (itemToRemove.modReqQuantity || 0)
      };
    }
    
    setSelectedConsumables(updatedConsumables);
  }, [selectedConsumables, alert]);

  // Handler for modifying consumables quantities - open modal
  const handleModifyConsumables = useCallback(() => {
    setOpenModifyModal(true);
  }, []);

  // Handler for closing the modify consumables modal
  const handleCloseModifyModal = useCallback(() => {
    setOpenModifyModal(false);
    setError(''); // Clear any validation errors when closing modal
  }, []);

  // Handler for confirming modified quantities
  const handleConfirmModification = useCallback(() => {
    // CRITICAL VALIDATION: Total of all modReqQuantity should not exceed sum of original quantities
    const totalModReqQuantity = selectedConsumables.reduce((sum, item) => 
      sum + (parseInt(item.modReqQuantity, 10) || 0), 0
    );
    
    // Calculate original total from base items only
    const baseItems = selectedConsumables.filter(item => !item.isSplit);
    const originalTotalQuantity = baseItems.reduce((sum, item) => 
      sum + (parseInt(item.originalQuantity, 10) || 0), 0
    );
    
    if (totalModReqQuantity > originalTotalQuantity) {
      alert.showWarning(`Total Requested Quantity across all rows (${totalModReqQuantity}) exceeds the original total requested quantity (${originalTotalQuantity}). Please adjust the quantities.`);
      return;
    }
    
    setOpenModifyModal(false);
  }, [selectedConsumables, alert]);

  // Handle rejecting the request
  const handleRejectRequest = useCallback(async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark explaining the reason for rejection.");
      return;
    }
    
    try {
      setIsUpdatingReq(true);
      const senttoData = {
        sent_to_tag: "store_bts_rejected",
        Text: remark,
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: row?.req_creator_id, 
          name: getSingleuser?.[row?.req_creator_id]?.name, 
          office: row?.req_data[0]?.sent_by?.office, 
          designation: row?.req_data[0]?.sent_by?.designation 
        },
        action: "Request rejected by Store Level 1",
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[row?.req_creator_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[row?.req_creator_id]?.designation?.[row?.req_data[0]?.sent_by?.office] || 'Unknown'}`,
        curr_status: "Rejected",
        time_stamp: new Date()
      };
      
      const body = { id: row?._id, senttoData: senttoData };
      
      await updateConsumableReq(body).unwrap();
      alert.showSuccess("Request has been rejected.", "Request Rejected", handleBack);
    } catch (err) {
      setError(`Failed to reject request: ${err.message || 'Unknown error'}`);
      console.error("Error rejecting request:", err);
      alert.showError("There was an error rejecting the request. Please try again.");
    } finally {
      setIsUpdatingReq(false);
    }
  }, [remark, auth, getSingleuser, user_current_office, row, updateConsumableReq, handleBack, alert]);
  
  // Normalize consumable for comparison
  const normalizeStoreConsumableForComparison = useCallback((item) => ({
    consumable: item.consumable,
    partno: item.partno,
    selectedQuantity: parseInt(item.selectedQuantity || item.quantity, 10),
    asset: item.asset || 'Not Applicable',
    assettagLabel: item.assettag?.label || 'Not Applicable'
  }), []);

  // Compare store consumable arrays
  const areStoreConsumablesEqual = useCallback((currentItems, previousItems) => {
    if (!Array.isArray(currentItems) || !Array.isArray(previousItems)) {
      return false;
    }
    
    // Quick check for array length difference
    if (currentItems.length !== previousItems.length) {
      return false;
    }
    
    // Normalize both arrays for comparison
    const normalizedCurrent = currentItems.map(normalizeStoreConsumableForComparison);
    const normalizedPrevious = previousItems.map(normalizeStoreConsumableForComparison);
    
    // Check if each item in current exists in previous with same properties
    return normalizedCurrent.every(currentItem => 
      normalizedPrevious.some(prevItem => 
        prevItem.consumable === currentItem.consumable &&
        prevItem.partno === currentItem.partno &&
        prevItem.selectedQuantity === currentItem.selectedQuantity &&
        prevItem.asset === currentItem.asset &&
        prevItem.assettagLabel === currentItem.assettagLabel
      )
    );
  }, [normalizeStoreConsumableForComparison]);

  // Process issue of consumables and forward to L2
  const handleForwardToL2 = useCallback(async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark before issuing consumables.");
      return;
    }

    if (!storeL2_id) {
      alert.showError("Store Level 2 approver information not found.");
      return;
    }

    // Filter items where Store L1 has assigned quantities for inventory update
    // This includes BOTH:
    // 1. Non-BO items (Store L1's own items) with quantity > 0
    // 2. BO-issued items where Store L1 has ADDED additional quantities (selectedQuantity > 0)
    const storeIssuedConsumables = selectedConsumables.filter(item => 
      item.partno && parseInt(item.selectedQuantity, 10) > 0
    );

    if (storeIssuedConsumables.length === 0) {
      alert.showWarning("Please select part numbers and set issue quantities for at least one item.");
      return;
    }
    
    // For comparison and saving, we need to include ALL items (BO + Store)
    // Include base rows even if they have 0 quantity (to preserve BO-issued records)
    const allIssuedConsumables = selectedConsumables.filter(item => {
      // Include if: has partno AND (has quantity > 0 OR is a base row)
      return item.partno && (parseInt(item.selectedQuantity, 10) > 0 || !item.isSplit);
    });

    try {
      setIsUpdatingReq(true);
      setError(''); // Clear any previous errors
      
      // Check if consumables have changed from previous store issue
      let previousStoreIssue = [];
      if (row?.store_consum_issue && row.store_consum_issue.length > 0) {
        const latestStoreIssue = row.store_consum_issue[row.store_consum_issue.length - 1];
        previousStoreIssue = latestStoreIssue.data || [];
      }
      
      const selectionUnchanged = areStoreConsumablesEqual(allIssuedConsumables, previousStoreIssue);
      
      // Check if this is a partial issue
      const latestRequest = row?.consumables_requested?.[row.consumables_requested.length - 1];
      const requestedItems = latestRequest?.data || [];
      
      // Check for each requested item if the total issued quantity matches (BO + Store combined)
      let isPartial = false;
      
      for (const reqItem of requestedItems) {
        // Find all issued items matching this request (handles splits from both BO and Store)
        const matchingIssued = allIssuedConsumables.filter(issItem => 
          issItem.consumable === reqItem.consumable && 
          issItem.asset === reqItem.asset &&
          (issItem.assettag?.label === reqItem.assettag?.label)
        );
        
        // Calculate total issued quantity for this request (BO + Store)
        const totalIssuedQty = matchingIssued.reduce((sum, issItem) => 
          sum + parseInt(issItem.selectedQuantity, 10), 0
        );
        
        // Calculate total requested quantity (original)
        const totalRequestedQty = parseInt(reqItem.quantity, 10);
        
        // If no items issued or quantity doesn't match, it's partial
        if (matchingIssued.length === 0 || totalIssuedQty < totalRequestedQty) {
          isPartial = true;
          break;
        }
      }
      
      // Determine if this is a partial or full issue
      const sent_to_tag = isPartial
        ? "storel1_bts_partialissue_l2"
        : "storel1_bts_fullissue_l2";
      
      const action = selectionUnchanged
        ? (isPartial 
            ? "Partial consumables re-forwarded to Store Level 2 (no changes)"
            : "All consumables re-forwarded to Store Level 2 (no changes)")
        : (isPartial
            ? "Partial consumables issued and forwarded to Store Level 2 for approval"
            : "All consumables issued and forwarded to Store Level 2 for approval");
      
      // Create the sent to data object
      const senttoData = {
        sent_to_tag,
        Text: remark,
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: storeL2_id, 
          name: getSingleuser?.[storeL2_id]?.name, 
          office: user_current_office, 
          designation: getSingleuser?.[storeL2_id]?.designation?.[user_current_office] 
        },
        action,
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[storeL2_id])}-${user_current_office}-${getSingleuser?.[storeL2_id]?.designation?.[user_current_office] || 'Unknown'}`,
        time_stamp: new Date()
      };

      // Only include store_consum_issue if consumables have changed
      // Save ALL issued consumables (BO + Store) for record keeping
      if (!selectionUnchanged || !row.store_consum_issue) {
        senttoData.store_consum_issue = {
          time_stamp: new Date(),
          status: "draft",
          data: allIssuedConsumables.map(item => ({
            ...item,
            quantity: item.selectedQuantity,
            modReqQuantity: item.modReqQuantity,
            isBoIssued: item.isBoIssued || false
          }))
        };
      }

      const body = { id: row?._id, senttoData: senttoData };
      
      // Only update inventory if consumables have changed
      // But ONLY for Store issued items, not BO issued items (BO already updated their inventory)
      if (!selectionUnchanged || !row.store_consum_issue) {
        // Update inventory - move items from balance to in-transit (only for Store issued items)
        console.log(`Updating inventory for ${storeIssuedConsumables.length} Store L1 items...`);
        
        await Promise.all(storeIssuedConsumables.map(async (item, index) => {
          console.log(`[${index + 1}/${storeIssuedConsumables.length}] Updating:`, {
            consumable: item.consumable,
            partno: item.partno,
            store: user_current_office,
            quantity: item.selectedQuantity
          });
          
          // Update consumable quantities - decrease balance and increase intransit
          const result = await updateconsumfinal({
            consumable: item.consumable,
            partno: item.partno,
            store: user_current_office,
            balanceQuantity: -parseInt(item.selectedQuantity), // Decrease balance
            isInTransit: parseInt(item.selectedQuantity)  // Increase in-transit
          }).unwrap();
          
          console.log(`[${index + 1}/${storeIssuedConsumables.length}] ✓ Success:`, result);
        }));
        
        console.log("✓ All inventory updates completed successfully");
      }
      
      // Update the request status
      try {
        await updateConsumableReq(body).unwrap();
      } catch (requestError) {
        console.error("Error updating request:", requestError);
        throw new Error(`Failed to update request: ${requestError?.data?.message || requestError.message || 'Unknown error'}`);
      }
      
      let responseMessage;
      if (selectionUnchanged) {
        responseMessage = `${isPartial ? 'Partial' : 'Full'} consumables re-forwarded to Store Level 2. No changes to consumables were made.`;
      } else {
        responseMessage = `${isPartial ? 'Partial' : 'Full'} consumables issue created and forwarded to Store Level 2 for approval.${isPartial ? ' Some requested items or quantities were not included in this issue.' : ''}`;
      }
      
      alert.showSuccess(responseMessage, "Success", handleBack);
    } catch (err) {
      console.error("=== CAUGHT ERROR IN handleForwardToL2 ===");
      console.error("Error object:", err);
      console.error("Error.message:", err?.message);
      console.error("Error.data:", err?.data);
      console.error("Error.status:", err?.status);
      
      const errorMessage = err.message || `Failed to forward to L2: ${err?.data?.message || err?.data || 'Unknown error'}`;
      setError(errorMessage);
      console.error("Final error message shown to user:", errorMessage);
      alert.showError(`Error: ${errorMessage}\n\nPlease try again.`);
    } finally {
      setIsUpdatingReq(false);
    }
  }, [
    remark, storeL2_id, selectedConsumables, row,
    auth, getSingleuser, user_current_office,
    areStoreConsumablesEqual,
    updateconsumfinal, updateConsumableReq, handleBack, alert
  ]);
console.log(selectedConsumables)
  // Memoize the TableRow rendering function
  const renderTableRows = useMemo(() => {
    return selectedConsumables.map((item, index) => (
      <TableRow 
        key={`consumable-${index}`}
        sx={item.isBoIssued ? { backgroundColor: '#f5f5f5' } : {}}
      >
        <TableCell>{index + 1}</TableCell>
        <TableCell>
          {item.consumable}
          {item.isBoIssued && (
            <span style={{ marginLeft: '8px', fontSize: '12px', color: '#1976d2', fontWeight: 'bold' }}>
              (BO Issued)
            </span>
          )}
        </TableCell>
        <TableCell>{item.asset || 'Not Applicable'}</TableCell>
        <TableCell>{item.assettag?.label || 'Not Applicable'}</TableCell>
        <TableCell>
          {item.isBoIssued ? (
            // Read-only display for BO issued items
            <TextField
              size="small"
              value={item.partno || ''}
              disabled
              sx={{ minWidth: 150 }}
            />
          ) : (
            // Editable autocomplete for Store L1 items
          <Autocomplete
            size="small"
            options={getPartNumberOptions(item.consumable, item.asset, item.assettag, index)}
            value={item.partno || null}
            onChange={(event, newValue) => handlePartNumberChange(index, newValue)}
            renderInput={(params) => <TextField {...params} label="Select Part No." />}
            sx={{ minWidth: 150 }}
          />
          )}
        </TableCell>
        <TableCell>
          {item.partno ? getAvailableQuantity(item.partno) : '-'}
        </TableCell>
        <TableCell>{item.modReqQuantity || item.quantity}</TableCell>
        <TableCell>{item.boIssuedQuantity || 0}</TableCell>
        <TableCell>
          {item.isBoIssued ? (
            // Display only for BO issued items
            <strong>{item.selectedQuantity}</strong>
          ) : (
            // Editable for Store L1 items (shown in modal)
            item.selectedQuantity
          )}
        </TableCell>
      </TableRow>
    ));
  }, [selectedConsumables, getAvailableQuantity, getPartNumberOptions, handlePartNumberChange]);

  return (
    <>
      {error && (
        <ErrorMessage>
          {error}
          <ErrorCloseButton onClick={() => setError('')}>×</ErrorCloseButton>
        </ErrorMessage>
      )}

      <RemarkContainer>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Add Remark"
          variant="outlined"
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
        />
      </RemarkContainer>
      
      {/* Display selected consumables in a table */}
        <SelectedAssetsContainer>
          <SelectedAssetsTitleContainer>
          <SelectedAssetsTitle>Requested Consumables</SelectedAssetsTitle>
            <ActionButtonsContainer>
            <Tooltip title="Modify quantities and split rows">
              <IconButton color="primary" onClick={handleModifyConsumables}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </ActionButtonsContainer>
          </SelectedAssetsTitleContainer>
           
          <StyledTableContainer component={Paper}>
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell>Sl.No.</TableCell>
                <TableCell>Consumable</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Asset Tag</TableCell>
                <TableCell>Part No.</TableCell>
                <TableCell>Store Available Qty</TableCell>
                <TableCell>Total Items Requested</TableCell>
                <TableCell>Issued at BO</TableCell>
                <TableCell>Issued at Store</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {renderTableRows}
            </TableBody>
          </Table>
          </StyledTableContainer>

      </SelectedAssetsContainer>

      {/* Action buttons */}
      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {/* Forward to L2 button - always visible, validation happens on click */}
        <ForwardButton onClick={handleForwardToL2} disabled={isUpdatingReq}>
          {isPartialIssueCheck() ? "SENT TO L2: ISSUE PARTIAL CONSUMABLES" : "SENT TO L2: ISSUE ALL CONSUMABLES"}
        </ForwardButton>
            
        <RejectButton onClick={handleRejectRequest} disabled={isUpdatingReq}>
          REJECT REQUEST
        </RejectButton>
      </div>

      {/* Modal for Modifying Consumables */}
      <ModifyConsumablesModal
        open={openModifyModal}
        onClose={handleCloseModifyModal}
        selectedConsumables={selectedConsumables}
        onQuantityChange={handleQuantityChange}
        onPartNumberChange={handlePartNumberChange}
        onSplitRow={handleSplitRow}
        onRemoveSplitRow={handleRemoveSplitRow}
        onConfirm={handleConfirmModification}
        getAvailableQuantity={getAvailableQuantity}
        getPartNumberOptions={getPartNumberOptions}
        getGroupTotalQuantity={getGroupTotalQuantity}
        availableQtyLabel="Store Available Qty"
        showBoIssuedColumn={true}
        error={error}
      />
    </>
  );
};

// Apply memo to component before exporting
const MemoizedStoreL1InboxFormCIR = React.memo(StoreL1InboxFormCIR);
export default MemoizedStoreL1InboxFormCIR;